import { useState } from "react";
import { useTranslation } from "react-i18next";
import { films } from "@/mocks/home";

const posterThemes = [
  "from-foreground-900 to-foreground-950",
  "from-accent-800 to-accent-950",
  "from-secondary-800 to-secondary-950",
];

export default function Films() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const activeFilm = films.find((film) => film.id === activeId) ?? null;

  return (
    <section id="films" className="w-full bg-background-100 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-400"></span>
              <span className="font-label text-sm uppercase tracking-[0.3em] text-primary-600">
                {t("films.kicker")}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              {t("films.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              {t("films.subtitle")}
            </p>
          </div>
          <a
            href="#news"
            className="inline-flex items-center gap-2 whitespace-nowrap font-label text-sm uppercase tracking-[0.16em] text-primary-700 hover:text-primary-600 border-b border-primary-300 pb-1 cursor-pointer"
          >
            {t("films.viewAll")}
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {films.map((film, index) => (
            <button
              key={film.id}
              type="button"
              onClick={() => setActiveId(film.id)}
              className="group text-left bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div
                className={`relative aspect-[4/5] bg-gradient-to-br ${posterThemes[index % posterThemes.length]} p-5 flex flex-col justify-between`}
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-background-50/15 text-background-50 font-label text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full">
                    <i className="ri-award-line"></i>
                    {film.badge}
                  </span>
                  <span className="font-label text-xs uppercase tracking-[0.16em] text-background-200">
                    {film.year}
                  </span>
                </div>
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 flex items-center justify-center rounded-full border border-background-50/25 text-background-50/50 group-hover:text-primary-300 group-hover:border-primary-300 transition-colors">
                  <i className="ri-play-fill text-3xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 leading-snug">
                    {film.title}
                  </h3>
                  <p className="mt-2 font-label text-xs uppercase tracking-[0.18em] text-background-200">
                    {film.format}
                  </p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm leading-relaxed text-foreground-600 line-clamp-2">
                  {film.logline}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.16em] text-primary-700 group-hover:text-primary-600">
                  {t("films.runtime")} · {film.runtime}
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeFilm && (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/70 flex items-center justify-center p-4"
          onClick={() => setActiveId(null)}
        >
          <div
            className="bg-background-50 rounded-lg w-full max-w-lg overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`relative h-40 bg-gradient-to-br ${posterThemes[
                films.findIndex((f) => f.id === activeFilm.id) % posterThemes.length
              ]} p-6 flex items-end`}
            >
              <button
                type="button"
                aria-label={t("nav.close")}
                onClick={() => setActiveId(null)}
                className="absolute top-4 right-4 flex items-center justify-center w-9 h-9 rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div>
                <p className="font-label text-xs uppercase tracking-[0.2em] text-background-200">
                  {activeFilm.format} · {activeFilm.year}
                </p>
                <h3 className="font-heading text-3xl font-bold text-background-50">
                  {activeFilm.title}
                </h3>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-900 font-label text-xs uppercase tracking-[0.16em] px-3 py-1 rounded-full">
                <i className="ri-award-line"></i>
                {activeFilm.badge}
              </span>
              <p className="mt-5 text-base leading-relaxed text-foreground-700">
                {activeFilm.logline}
              </p>
              <div className="mt-6 flex items-center gap-6 pt-5 border-t border-background-200">
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.16em] text-foreground-400">
                    {t("films.runtime")}
                  </p>
                  <p className="font-heading text-lg font-semibold text-foreground-900">
                    {activeFilm.runtime}
                  </p>
                </div>
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.16em] text-foreground-400">
                    {t("films.title")}
                  </p>
                  <p className="font-heading text-lg font-semibold text-foreground-900">
                    {activeFilm.year}
                  </p>
                </div>
              </div>
              <a
                href="#contact"
                onClick={() => setActiveId(null)}
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-accent-950 font-label text-sm uppercase tracking-[0.14em] px-5 py-3 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-mail-send-line"></i>
                {t("news.readMore")}
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}